import { useState, useRef } from 'react';
import { Button, Modal, Form, Alert, ProgressBar } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import CryptoJS from 'crypto-js';

// Redux-Functions
import { selectLanguageUse } from '../../../redux/slices/language.Slice';

// Utility Service
import { getToken, getUser } from "../../../utility/AuthService";

// API service
import { endSubmissionService, endProgressSubmissionService } from '../../../services/submissionService';
import { NotificationTypes } from '../../../services/notificationService';
import { notificationQueueService } from '../../../services/notificationQueueService';

const SubmissionEndModal = (props) => {
    const authToken = getToken()
    const location = useLocation()
    const navigate = useNavigate()
    const fileInputRef = useRef(null)

    const [confirmation, setConfirmation] = useState(false)
    const [showUpload, setShowUpload] = useState(false)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState(0)
    const [uploadError, setUploadError] = useState('')
    const Separation_Url = process.env.REACT_APP_SEPARATION

    const user = getUser()
    const languageUse = useSelector(selectLanguageUse || user?.language)

    // File upload handlers
    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        setUploadedFiles(prev => [...prev, ...files]);
        setUploadError('');
    };

    const removeFile = (index) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (uploadedFiles.length === 0) {
            setUploadError(languageUse === 'English' ? 'Please select at least one file to upload.' : 'Veuillez sélectionner au moins un fichier à télécharger.');
            return;
        }

        setUploading(true);
        setUploadProgress(0);
        setUploadError('');

        try {
            // Simulate file upload progress
            for (let i = 0; i <= 100; i += 10) {
                setUploadProgress(i);
                await new Promise(resolve => setTimeout(resolve, 100));
            }

            // Store files in localStorage for now (in real app, you'd upload to server)
            const fileData = uploadedFiles.map(file => ({
                name: file.name,
                size: file.size,
                type: file.type,
                lastModified: file.lastModified
            }));
            
            localStorage.setItem(`uploaded_documents_${props.submission_id}`, JSON.stringify(fileData));
            
            toast.success(languageUse === 'English' ? 'Documents uploaded successfully!' : 'Documents téléchargés avec succès!');
            setShowUpload(false);
            
            // Show confirmation for final submission
            setConfirmation(true);
            
        } catch (error) {
            setUploadError(languageUse === 'English' ? 'Upload failed. Please try again.' : 'Le téléchargement a échoué. Veuillez réessayer.');
            toast.error(languageUse === 'English' ? 'Upload failed!' : 'Échec du téléchargement!');
        } finally {
            setUploading(false);
            setUploadProgress(0);
        }
    };

    /**
        * @function endSubmission
        * @param
        * @description handle api to End the assesment  
    */
    const endSubmission = async () => {
        try {
            if (location?.pathname?.includes('uploadProgressDocuments') || location?.pathname?.includes('progress')) {
                // Get all saved answers before ending submission
                const allAnswers = {};
                const overallProgress = JSON.parse(localStorage.getItem(`progress_overall_${props.submission_id}`) || '{}');
                
                // Collect all dimension answers
                Object.keys(overallProgress).forEach(dimensionId => {
                    const savedAnswers = localStorage.getItem(`progress_answers_${props.submission_id}_${dimensionId}`);
                    if (savedAnswers) {
                        allAnswers[dimensionId] = JSON.parse(savedAnswers);
                    }
                });
                
                const result = await endProgressSubmissionService({
                    authToken,
                    SubmissionId: props.submission_id,
                    answers: allAnswers // Send all answers for final processing
                });
                
                const encryptedSubId = CryptoJS.AES.encrypt(props.submission_id, 'mySecretKey').toString();
                
                // Clear progress data after successful submission
                Object.keys(overallProgress).forEach(dimensionId => {
                    localStorage.removeItem(`progress_answers_${props.submission_id}_${dimensionId}`);
                });
                localStorage.removeItem(`progress_overall_${props.submission_id}`);
                
                // Send submission completed notification
                await notificationQueueService.addToQueue({
                    type: NotificationTypes.SUBMISSION_COMPLETED,
                    userId: user.id,
                    email: user.email,
                    data: {
                        username: user.username,
                        submissionId: props.submission_id
                    },
                    priority: 'HIGH'
                });
                
                toast.success(result?.data?.data?.message)
                props.handleClose()
                
                // Navigate to dashboard with report generation flag
                navigate(`/user/progress/dashboard/${encodeURIComponent(encryptedSubId)}?generateReport=true`)
            } else {
                const result = await endSubmissionService({
                    authToken,
                    SubmissionId: props.submission_id
                });
                
                const encryptedSubId = CryptoJS.AES.encrypt(props.submission_id, 'mySecretKey').toString();
                
                // Clear any saved progress
                localStorage.removeItem(`assessment_progress_${props.submission_id}`);
                
                // Send submission completed notification
                await notificationQueueService.addToQueue({
                    type: NotificationTypes.SUBMISSION_COMPLETED,
                    userId: user.id,
                    email: user.email,
                    data: {
                        username: user.username,
                        submissionId: props.submission_id
                    },
                    priority: 'HIGH'
                });
                
                toast.success(result?.data?.data?.message)
                props.handleClose()
                
                // Navigate to dashboard with report generation flag
                navigate(`/user/dashboard/${encodeURIComponent(encryptedSubId)}?generateReport=true`)
            }
        } catch (error) {
            console.error('Submission end error:', error);
            toast.error(error?.response?.data?.message || 'Failed to end submission. Please try again.');
        }
    }

    // Debug logging
    console.log('SubmissionEndModal props:', {
        show: props.show,
        questionPage: props?.questionPage,
        submission_id: props?.submission_id,
        decryptedSubId: props?.decryptedSubId,
        confirmation: confirmation
    });

    return <>
        <div>
            <Toaster />
        </div>
        <Modal show={props.show} onHide={props.handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>
                    {languageUse === 'English' ? 'Assessment Complete!' : 'Évaluation terminée!'}
                </Modal.Title>
            </Modal.Header>
            
            {/* on ducuments submission page */}
            {!props?.questionPage && !confirmation && <Modal.Body>
                {Separation_Url === "ESG" && <>
                    {languageUse === 'English' ? 'By submitting this survey, I confirm that:' : 'En soumettant cette enquête, je confirme que :'}
                    <ul className='mb-0'>
                        <li>{languageUse === 'English' ? 'All information provided is accurate and truthful.' : 'Toutes les informations fournies sont exactes et véridiques.'}</li>
                        <li>{languageUse === 'English' ? 'The data may be used to generate an ESG maturity score and peer benchmark.' : 'Les données peuvent être utilisées pour générer un score de maturité ESG et une analyse comparative.'}</li>
                        <li>{languageUse === 'English' ? 'My individual data will be kept confidential.' : 'Mes données individuelles resteront confidentielles.'}</li>
                        <li>{languageUse === 'English' ? 'Aggregated data may be shared with the reporting company.' : 'Les données agrégées peuvent être partagées avec l\'entreprise déclarante.'}</li>
                    </ul>
                </>}
                {Separation_Url === "PROGRESS" && <>
                    {languageUse === 'English' ? 'By submitting this survey, I confirm that:' : 'En soumettant cette enquête, je confirme que :'}
                    <ul className='mb-0'>
                        <li>{languageUse === 'English' ? 'All information provided is accurate and truthful.' : 'Toutes les informations fournies sont exactes et véridiques.'}</li>
                        <li>{languageUse === 'English' ? 'The data may be used to generate SME ESG Climate Action Readiness report and peer benchmark.' : 'Les données peuvent être utilisées pour générer le rapport de Préparation à l\'Action Climatique SME ESG et une analyse comparative.'}</li>
                        <li>{languageUse === 'English' ? 'My individual data will be kept confidential.' : 'Mes données individuelles resteront confidentielles.'}</li>
                        <li>{languageUse === 'English' ? 'Aggregated data may be shared with the reporting company.' : 'Les données agrégées peuvent être partagées avec l\'entreprise déclarante.'}</li>
                    </ul>
                </>}
            </Modal.Body>}

            {/* on answer submission page - NEW INLINE UPLOAD */}
            {props?.questionPage && !confirmation && !showUpload && <Modal.Body>
                <div className="text-center mb-4">
                    <h5 className="text-success mb-3">
                        🎉 {languageUse === 'English' ? 'Congratulations! Your assessment is complete!' : 'Félicitations! Votre évaluation est terminée!'}
                    </h5>
                    <p className="text-muted">
                        {languageUse === 'English' 
                            ? 'Would you like to upload supporting documents to strengthen your ESG assessment? This will help provide a more comprehensive evaluation.'
                            : 'Souhaitez-vous télécharger des documents justificatifs pour renforcer votre évaluation ESG? Cela aidera à fournir une évaluation plus complète.'
                        }
                    </p>
                </div>
                
                <div className="d-grid gap-2">
                    <Button 
                        variant="outline-primary" 
                        size="lg"
                        onClick={() => setShowUpload(true)}
                        className="mb-2"
                    >
                        📁 {languageUse === 'English' ? 'Yes, Upload Documents' : 'Oui, télécharger des documents'}
                    </Button>
                    
                    <Button 
                        variant="outline-secondary" 
                        size="lg"
                        onClick={() => setConfirmation(true)}
                    >
                        🚀 {languageUse === 'English' ? 'No, Complete Assessment Now' : 'Non, terminer l\'évaluation maintenant'}
                    </Button>
                </div>
            </Modal.Body>}

            {/* NEW: Inline Upload Interface */}
            {props?.questionPage && !confirmation && showUpload && <Modal.Body>
                <div className="text-center mb-4">
                    <h5 className="text-primary mb-3">
                        📁 {languageUse === 'English' ? 'Upload Supporting Documents' : 'Télécharger des documents justificatifs'}
                    </h5>
                    <p className="text-muted">
                        {languageUse === 'English' 
                            ? 'Select files that support your assessment answers (PDF, Word, Excel, Images)'
                            : 'Sélectionnez des fichiers qui étayent vos réponses d\'évaluation (PDF, Word, Excel, Images)'
                        }
                    </p>
                </div>

                {/* File Upload Area */}
                <div className="border-2 border-dashed border-secondary rounded p-4 text-center mb-3" 
                     style={{borderStyle: 'dashed'}}>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                        style={{display: 'none'}}
                    />
                    <Button 
                        variant="outline-primary" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                    >
                        📂 {languageUse === 'English' ? 'Choose Files' : 'Choisir des fichiers'}
                    </Button>
                    <p className="text-muted mt-2 mb-0">
                        {languageUse === 'English' 
                            ? 'Drag and drop files here or click to browse'
                            : 'Glissez-déposez des fichiers ici ou cliquez pour parcourir'
                        }
                    </p>
                </div>

                {/* Selected Files List */}
                {uploadedFiles.length > 0 && (
                    <div className="mb-3">
                        <h6>{languageUse === 'English' ? 'Selected Files:' : 'Fichiers sélectionnés:'}</h6>
                        {uploadedFiles.map((file, index) => (
                            <div key={index} className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
                                <div>
                                    <strong>{file.name}</strong>
                                    <small className="text-muted d-block">
                                        {(file.size / 1024 / 1024).toFixed(2)} MB
                                    </small>
                                </div>
                                <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => removeFile(index)}
                                    disabled={uploading}
                                >
                                    ❌
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Upload Progress */}
                {uploading && (
                    <div className="mb-3">
                        <ProgressBar 
                            now={uploadProgress} 
                            label={`${uploadProgress}%`}
                            variant="success"
                        />
                        <small className="text-muted">
                            {languageUse === 'English' ? 'Uploading files...' : 'Téléchargement des fichiers...'}
                        </small>
                    </div>
                )}

                {/* Error Message */}
                {uploadError && (
                    <Alert variant="danger" className="mb-3">
                        {uploadError}
                    </Alert>
                )}

                {/* Upload Actions */}
                <div className="d-flex gap-2 justify-content-end">
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowUpload(false)}
                        disabled={uploading}
                    >
                        ← {languageUse === 'English' ? 'Back' : 'Retour'}
                    </Button>
                    
                    <Button 
                        variant="primary" 
                        onClick={handleUpload}
                        disabled={uploading || uploadedFiles.length === 0}
                    >
                        {uploading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                {languageUse === 'English' ? 'Uploading...' : 'Téléchargement...'}
                            </>
                        ) : (
                            <>
                                📤 {languageUse === 'English' ? 'Upload & Continue' : 'Télécharger et continuer'}
                            </>
                        )}
                    </Button>
                </div>
            </Modal.Body>}

            {confirmation && <Modal.Body>
                <div className="text-center mb-4">
                    <h5 className="text-warning mb-3">
                        ⚠️ {languageUse === 'English' ? 'Final Confirmation' : 'Confirmation finale'}
                    </h5>
                    
                    {uploadedFiles.length > 0 && (
                        <Alert variant="success" className="mb-3">
                            ✅ {languageUse === 'English' 
                                ? `Successfully uploaded ${uploadedFiles.length} document(s)`
                                : `${uploadedFiles.length} document(s) téléchargé(s) avec succès`
                            }
                        </Alert>
                    )}
                    
                    {Separation_Url === "ESG" && <>
                        <p className="mb-3">{languageUse === 'English' ? 'By submitting this survey, I confirm that:' : 'En soumettant cette enquête, je confirme que :'}</p>
                        <ul className='mb-0 text-start'>
                            <li>{languageUse === 'English' ? 'All information provided is accurate and truthful.' : 'Toutes les informations fournies sont exactes et véridiques.'}</li>
                            <li>{languageUse === 'English' ? 'The data may be used to generate an ESG maturity score and peer benchmark.' : 'Les données peuvent être utilisées pour générer un score de maturité ESG et une analyse comparative.'}</li>
                            <li>{languageUse === 'English' ? 'My individual data will be kept confidential.' : 'Mes données individuelles resteront confidentielles.'}</li>
                            <li>{languageUse === 'English' ? 'Aggregated data may be shared with the reporting company.' : 'Les données agrégées peuvent être partagées avec l\'entreprise déclarante.'}</li>
                        </ul>
                    </>}
                    {Separation_Url === "PROGRESS" && <>
                        <p className="mb-3">{languageUse === 'English' ? 'By submitting this survey, I confirm that:' : 'En soumettant cette enquête, je confirme que :'}</p>
                        <ul className='mb-0 text-start'>
                            <li>{languageUse === 'English' ? 'All information provided is accurate and truthful.' : 'Toutes les informations fournies sont exactes et véridiques.'}</li>
                            <li>{languageUse === 'English' ? 'The data may be used to generate SME ESG Climate Action Readiness report and peer benchmark.' : 'Les données peuvent être utilisées pour générer le rapport de Préparation à l\'Action Climatique SME ESG et une analyse comparative.'}</li>
                            <li>{languageUse === 'English' ? 'My individual data will be kept confidential.' : 'Mes données individuelles resteront confidentielles.'}</li>
                            <li>{languageUse === 'English' ? 'Aggregated data may be shared with the reporting company.' : 'Les données agrégées peuvent être partagées avec l\'entreprise déclarante.'}</li>
                        </ul>
                    </>}
                </div>
            </Modal.Body>}

            {/* Footer Actions */}
            {!confirmation && !props?.questionPage && <Modal.Footer>
                <Button variant="secondary" type='button' onClick={props.handleClose}>
                    {languageUse === 'English' ? 'Close' : 'Fermer'}
                </Button>
                <Button variant="primary" type='submit' onClick={endSubmission}>
                    {languageUse === 'English' ? 'Yes' : 'Oui'}
                </Button>
            </Modal.Footer>}

            {confirmation && <Modal.Footer className='d-flex justify-content-between'>
                <Button variant="primary" type='submit' onClick={endSubmission}>
                    🚀 {languageUse === 'English' ? 'Yes, Submit Assessment' : 'Oui, soumettre l\'évaluation'}
                </Button>

                <Button variant="outline-secondary" type='button' onClick={() => {
                    setConfirmation(false)
                    if (uploadedFiles.length > 0) {
                        setShowUpload(true)
                    }
                }}>
                    ← {languageUse === 'English' ? 'Back' : 'Retour'}
                </Button>
            </Modal.Footer>}

        </Modal>
    </>
}

export default SubmissionEndModal;